import { z } from "zod";
import {
  BatchId,
  InventoryId,
  IsoTimestamp,
  NightId,
  OrderId,
  PlayerId,
  PoolId,
  RollId,
  VaultItemId,
} from "./ids.js";

// ── THE VAULT lifecycle v0.2 (THE VAULT §2) ──────────────────────────────────
// BLG coins and XP NEVER pass through the Vault — they grant instantly. The
// Vault is exclusively for value requiring fulfilment or an explicit claim.
//
//   (vault_item_granted creates the item in AWARDED)
//   AWARDED ──── vault_item_ready ────▶ READY        (banked, visible in the locker)
//   READY ────── vault_claim_started ─▶ CLAIMING     (player tapped; fulfilment in flight)
//   CLAIMING ─── vault_fulfilled ─────▶ FULFILLED    (code delivered / airtime landed / payout sent)
//   CLAIMING ─── vault_fulfil_failed ─▶ FAILED
//   FAILED ───── vault_claim_retried ─▶ CLAIMING     (auto-retry ×3)
//   FAILED ───── vault_converted ─────▶ CONVERTED    (fallback per item policy, apology copy)
//   FAILED ───── vault_escalated ─────▶ ESCALATED    (ops triage — DUGOUT payout queue)
//   ESCALATED ── vault_fulfilled ─────▶ FULFILLED    (manual fulfil)
//   ESCALATED ── vault_converted ─────▶ CONVERTED
//   READY ────── vault_expiring ──────▶ EXPIRING     (7-day warning window; T-7d + T-1d
//   EXPIRING ─── vault_claim_started ─▶ CLAIMING      protected-tier notifications)
//   EXPIRING ─── vault_converted ─────▶ CONVERTED    (policy: value → coins, logged)
//   EXPIRING ─── vault_expired ───────▶ EXPIRED      (per-policy; never silent)
export const VaultItemState = z.enum([
  "AWARDED",
  "READY",
  "CLAIMING",
  "FULFILLED",
  "FAILED",
  "CONVERTED",
  "ESCALATED",
  "EXPIRING",
  "EXPIRED",
]);
export type VaultItemState = z.infer<typeof VaultItemState>;

export const VAULT_TERMINAL_STATES: readonly VaultItemState[] = [
  "FULFILLED",
  "CONVERTED",
  "EXPIRED",
];

// THE VAULT §1 — what lives here. Coins/XP excluded by design.
export const VaultItemType = z.enum([
  "airtime_voucher",
  "coin_voucher", // in-game coin voucher
  "cash_prize",
  "physical",
  "banked_drop", // MOMENTS drop revealed but not claim-tapped before session end
]);
export type VaultItemType = z.infer<typeof VaultItemType>;

// Discriminated source — 'moment_drop:{roll_id}' | 'store_purchase:{order_id}' | 'prize_payout:{night_id}'
export const VaultSource = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("moment_drop"), roll_id: RollId }),
  z.object({ kind: z.literal("store_purchase"), order_id: OrderId }),
  z.object({ kind: z.literal("prize_payout"), night_id: NightId }),
]);
export type VaultSource = z.infer<typeof VaultSource>;

export const VaultItem = z.object({
  id: VaultItemId,
  player_id: PlayerId,
  type: VaultItemType,
  state: VaultItemState,
  source: VaultSource,
  pool_id: PoolId.nullable(),
  inventory_id: InventoryId.nullable(), // INVARIANT: voucher item never READY without this or a live-API pool
  value_minor: z.number().int().min(0),
  expiry: IsoTimestamp.nullable(),
  fulfilment_ref: z.string().nullable(),
  created_at: IsoTimestamp,
});
export type VaultItem = z.infer<typeof VaultItem>;

// ── Voucher inventory (one truth for won AND bought) ─────────────────────────
//   AVAILABLE ── voucher_reserved ─▶ RESERVED    (row-locked, atomic with grant)
//   RESERVED ─── voucher_assigned ─▶ ASSIGNED    (claim fulfilment)
//   RESERVED ─── voucher_released ─▶ AVAILABLE   (vault conversion returns stock)
//   ASSIGNED ─── voucher_redeemed ─▶ REDEEMED    (provider confirms)
//   AVAILABLE ── voucher_expired ──▶ EXPIRED
//   AVAILABLE ── voucher_voided ───▶ VOID        (ops pulls a batch)
export const VoucherStatus = z.enum([
  "AVAILABLE",
  "RESERVED",
  "ASSIGNED",
  "REDEEMED",
  "EXPIRED",
  "VOID",
]);
export type VoucherStatus = z.infer<typeof VoucherStatus>;

export const VOUCHER_TERMINAL_STATES: readonly VoucherStatus[] = ["REDEEMED", "EXPIRED", "VOID"];

export const FulfilmentMode = z.enum(["api_realtime", "code_pool"]);
export type FulfilmentMode = z.infer<typeof FulfilmentMode>;

export const VoucherPool = z.object({
  id: PoolId,
  provider: z.string(), // 'airtime_mtn' | 'airtime_voda' | 'eafc_points' | …
  type: z.string(),
  denomination_minor: z.number().int().min(0),
  currency: z.string().length(3),
  fulfilment_mode: FulfilmentMode,
  cost_minor: z.number().int().min(0),
  active: z.boolean(),
});
export type VoucherPool = z.infer<typeof VoucherPool>;

export const VoucherInventoryRow = z.object({
  id: InventoryId,
  pool_id: PoolId,
  code_enc: z.string().nullable(), // encrypted at rest; NULL for api_realtime
  status: VoucherStatus,
  reserved_by_vault_item: VaultItemId.nullable(),
  reserved_at: IsoTimestamp.nullable(),
  assigned_at: IsoTimestamp.nullable(),
  expires_at: IsoTimestamp.nullable(),
  batch_id: BatchId.nullable(),
});
export type VoucherInventoryRow = z.infer<typeof VoucherInventoryRow>;

export const VoucherBatch = z.object({
  id: BatchId,
  pool_id: PoolId,
  loaded_by: z.string().min(1), // ops identity, audited
  loaded_at: IsoTimestamp,
  count: z.number().int().min(1),
  source_ref: z.string().nullable(),
});
export type VoucherBatch = z.infer<typeof VoucherBatch>;
